/**
 * ScrollTo
 * ---------------------------------------------------------------------------
 * Native JavaScript scroll-to utility.
 *
 * Finds elements with a `data-scroll-to` attribute and smoothly scrolls to
 * either a CSS selector or an absolute Y position.
 *
 * ---------------------------------------------------------------------------
 * EXAMPLES
 * ---------------------------------------------------------------------------
 *
 * Scroll to an element:
 *
 * <a href="#" data-scroll-to="#about">
 *     About
 * </a>
 *
 * Scroll to an element with an offset:
 *
 * <a href="#" data-scroll-to="#about, -100">
 *     About
 * </a>
 *
 * Scroll to an absolute Y position:
 *
 * <a href="#" data-scroll-to="500">
 *     Scroll to 500px
 * </a>
 *
 * Positive offsets move the scroll position down:
 *
 * <a href="#" data-scroll-to="#about, 50">
 *     About
 * </a>
 *
 * Negative offsets move the scroll position up:
 *
 * <a href="#" data-scroll-to="#about, -100">
 *     About
 * </a>
 *
 * ---------------------------------------------------------------------------
 * CSS
 * ---------------------------------------------------------------------------
 *
 * You can use `scroll-padding-top` to account for a fixed/sticky header:
 *
 * html {
 *     scroll-padding-top: var(--header-height);
 * }
 *
 * The module also respects the user's `prefers-reduced-motion` preference.
 *
 * ---------------------------------------------------------------------------
 */

class ScrollTo {
    constructor() {}

    /**
     * Initialise scroll-to triggers.
     */
    build() {
        const attrName = 'data-scroll-to';

        document.querySelectorAll(`[${attrName}]`).forEach((element) => {
            element.addEventListener('click', (e) => {
                e.preventDefault();

                this.scrollTo(
                    e.currentTarget.getAttribute(attrName)
                );
            });
        });
    }

    /**
     * Scroll to a target.
     *
     * @param {string} attrs
     */
    scrollTo(attrs) {
        if (!attrs) {
            console.error('No scroll target specified');
            return;
        }

        const attribute = attrs.split(',');

        let y = attribute[0].trim();
        const offset = attribute[1]
            ? parseInt(attribute[1].trim(), 10)
            : 0;

        /**
         * If the target isn't a number, treat it as a CSS selector.
         */
        if (!this._isNum(y)) {
            let targetElement;

            try {
                targetElement = document.querySelector(y);
            } catch (error) {
                console.error(
                    `Invalid scroll target selector: ${y}`
                );

                return;
            }

            // If no element is found, exit.
            if (!targetElement) {
                console.error(
                    `No element found for scroll target: ${y}`
                );

                return;
            }

            /**
             * Get the element's position relative to the document.
             */
            y =
                targetElement.getBoundingClientRect().top +
                window.scrollY;
        } else {
            /**
             * Convert an absolute Y position to a number.
             */
            y = parseFloat(y);
        }

        /**
         * Account for CSS `scroll-padding-top`.
         *
         * This is particularly useful when the site has a fixed header.
         *
         * Example:
         *
         * html {
         *     scroll-padding-top: 100px;
         * }
         */
        const scrollPaddingTop =
            parseInt(
                window
                    .getComputedStyle(document.documentElement)
                    .scrollPaddingTop,
                10
            ) || 0;

        y -= scrollPaddingTop;

        /**
         * Apply the supplied offset.
         */
        y += offset;

        /**
         * Prevent scrolling above the top of the document.
         */
        y = Math.max(0, y);

        /**
         * Respect the user's reduced-motion preference.
         *
         * If the user has requested reduced motion, the browser will jump
         * directly to the target rather than animating the scroll.
         */
        const prefersReducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        ).matches;

        /**
         * Use the native browser scrolling API.
         *
         * No GSAP or ScrollToPlugin required.
         */
        window.scrollTo({
            top: y,
            left: 0,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
    }

    /**
     * Check whether a value is numeric.
     *
     * @param {string|number} value
     * @returns {boolean}
     */
    _isNum(value) {
        return value !== '' && !isNaN(value);
    }
}

export default new ScrollTo();
